import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData } from "../../services/request"

const infoRow = (label, value) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminPaymentDetails = () => {
  const { paymentId } = useParams()
  const [payment, setPayment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const fetchPayment = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/payments/${paymentId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setPayment(res.result.data?.data || null)
      } else {
        setError(res.result.data?.message || 'Payment not found')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchPayment()
  }, [paymentId])

  const formatCurrency = (amount, currency = 'NGN') => {
    const num = Number(amount)
    if (isNaN(num)) return amount
    return `${currency === 'NGN' ? '₦' : currency} ${num.toLocaleString()}`
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'initiated':
        return 'bg-blue-100 text-blue-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'failed':
        return 'bg-red-100 text-red-700'
      case 'cancelled':
        return 'bg-gray-100 text-gray-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <Link to="/admin/app/payments" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
        ← Back to payments
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
          <p className="mt-1 text-sm text-gray-600">Payment ID: {paymentId}</p>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
          ) : !payment ? (
            <div className="text-sm text-gray-500">Payment not found.</div>
          ) : (
            <div className="space-y-6">
              {/* Payment Status */}
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">Status:</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(payment.status)}`}>
                    {payment.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(payment.amount, payment.currency)}
                </div>
              </div>

              {/* Payment Information */}
              <div className="rounded-lg border border-gray-100">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <h2 className="text-sm font-semibold text-gray-900">Payment Information</h2>
                </div>
                <div className="px-4">
                  {infoRow('Transaction ID', payment.transactionId)}
                  {infoRow('Transaction Reference', payment.txRef)}
                  {infoRow('Amount', formatCurrency(payment.amount, payment.currency))}
                  {infoRow('Currency', payment.currency)}
                  {infoRow('Payment Type', payment.paymentType || '—')}
                  {infoRow('Payment Method', payment.paymentMethod || '—')}
                  {infoRow('Verified By', payment.verifiedBy || '—')}
                  {infoRow('Verified At', payment.verifiedAt ? formatDate(payment.verifiedAt) : '—')}
                  {infoRow('Created At', formatDate(payment.createdAt))}
                  {infoRow('Updated At', formatDate(payment.updatedAt))}
                </div>
              </div>

              {/* Related Links */}
              <div className="rounded-lg border border-gray-100">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <h2 className="text-sm font-semibold text-gray-900">Related Information</h2>
                </div>
                <div className="px-4 py-3 space-y-3">
                  {payment.userId?._id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Patient:</span>
                      <Link
                        to={`/admin/app/patients/${payment.userId._id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      >
                        View Patient Details →
                      </Link>
                    </div>
                  )}
                  {payment.hospitalId?._id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Hospital:</span>
                      <Link
                        to={`/admin/app/hospitals/${payment.hospitalId._id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      >
                        View Hospital Details →
                      </Link>
                    </div>
                  )}
                  {payment.postId?._id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Request:</span>
                      <Link
                        to={`/admin/app/requests/${payment.postId._id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      >
                        View Request Details →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Information (from payment gateway) */}
              {(payment.customerName || payment.customerEmail || payment.customerPhone) && (
                <div className="rounded-lg border border-gray-100">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h2 className="text-sm font-semibold text-gray-900">Customer Information (Payment Gateway)</h2>
                  </div>
                  <div className="px-4">
                    {infoRow('Name', payment.customerName || '—')}
                    {infoRow('Email', payment.customerEmail || '—')}
                    {infoRow('Phone', payment.customerPhone || '—')}
                  </div>
                </div>
              )}

              {/* Processor Response */}
              {payment.processorResponse && (
                <div className="rounded-lg border border-gray-100">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h2 className="text-sm font-semibold text-gray-900">Processor Response</h2>
                  </div>
                  <div className="px-4 py-3">
                    <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono bg-gray-50 p-3 rounded border">
                      {payment.processorResponse}
                    </pre>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {payment.metadata && Object.keys(payment.metadata).length > 0 && (
                <div className="rounded-lg border border-gray-100">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h2 className="text-sm font-semibold text-gray-900">Additional Metadata</h2>
                  </div>
                  <div className="px-4 py-3">
                    <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono bg-gray-50 p-3 rounded border">
                      {JSON.stringify(payment.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminPaymentDetails

